import { useState, useEffect } from 'react';
import { getLatestPrices, getPriceHistory, getLatestAnalysis, seedDemoData } from '../services/api';
import { sortContracts, deduplicateContracts } from '../utils/contractUtils';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { TrendingUp, TrendingDown, Zap, Flame, Activity, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import PriceChart from '../components/PriceChart';

const MarketPage = () => {
  const [prices, setPrices] = useState([]);
  const [analysis, setAnalysis] = useState(null);
  const [selectedContract, setSelectedContract] = useState(null);
  const [priceHistory, setPriceHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('electricity');

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedContract) {
      fetchPriceHistory(selectedContract);
    }
  }, [selectedContract]);

  const fetchData = async () => {
    try {
      const [pricesRes, analysisRes] = await Promise.all([
        getLatestPrices(),
        getLatestAnalysis()
      ]);
      setPrices(pricesRes.data);
      setAnalysis(analysisRes.data);
      
      // Set default selected contract
      const elecPrices = pricesRes.data.filter(p => p.energy_type === 'electricity');
      if (elecPrices.length > 0) {
        setSelectedContract(elecPrices[0].contract_name);
      }
    } catch (error) {
      console.error('Error fetching market data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPriceHistory = async (contractName) => {
    setHistoryLoading(true);
    try {
      const response = await getPriceHistory(contractName, 90);
      setPriceHistory(response.data);
    } catch (error) {
      console.error('Error fetching price history:', error);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleSeedData = async () => {
    try {
      await seedDemoData();
      toast.success('Données de démonstration chargées !');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
    }
  };

  // Deduplicate contracts: keep most recent entry per base contract name
  const electricityPrices = sortContracts(deduplicateContracts(prices.filter(p => p.energy_type === 'electricity')));
  const gasPrices = sortContracts(deduplicateContracts(prices.filter(p => p.energy_type === 'gas')));
  const currentPrices = activeTab === 'electricity' ? electricityPrices : gasPrices;
  // All deduplicated contracts for chart dropdown
  const allContracts = sortContracts(deduplicateContracts(prices));
  const currentContractData = prices.find(p => p.contract_name === selectedContract);

  const PriceRow = ({ price }) => (
    <div 
      className={`flex items-center justify-between p-4 rounded-lg hover:bg-[#397DFF]/5 transition-colors cursor-pointer ${
        selectedContract === price.contract_name ? 'bg-[#397DFF]/10 border border-[#397DFF]/30' : 'bg-[#F5F6F9]'
      }`}
      onClick={() => setSelectedContract(price.contract_name)}
      data-testid={`price-row-${price.contract_name}`}
    >
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
          price.energy_type === 'gas' ? 'bg-[#397DFF]/10' : 'bg-[#F9C80E]/10'
        }`}>
          {price.energy_type === 'gas' ? (
            <Flame className="w-5 h-5 text-[#397DFF]" />
          ) : (
            <Zap className="w-5 h-5 text-[#F9C80E]" />
          )}
        </div>
        <div>
          <p className="font-bold text-[#191D1E]">{price.contract_name}</p>
          <p className="text-xs text-[#687175] capitalize">{price.contract_type}</p>
        </div>
      </div>
      <div className="text-right">
        <p className="font-mono font-bold text-[#191D1E]">{price.price?.toFixed(2)} €</p>
        {price.variation_pct !== null && (
          <div className={`flex items-center justify-end gap-1 text-sm ${
            price.variation_pct >= 0 ? 'text-red-500' : 'text-[#10D183]'
          }`}>
            {price.variation_pct >= 0 ? (
              <TrendingUp className="w-3 h-3" />
            ) : (
              <TrendingDown className="w-3 h-3" />
            )}
            <span>{price.variation_pct >= 0 ? '+' : ''}{price.variation_pct?.toFixed(2)}%</span>
          </div>
        )}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="space-y-6" data-testid="market-loading">
        <Skeleton className="h-10 w-48 bg-white" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Skeleton className="h-96 bg-white" />
          <Skeleton className="h-96 bg-white" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="market-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#191D1E]">Prix du marché</h1>
          <p className="text-[#687175] mt-1">Dernière mise à jour : {prices.length > 0 ? prices.reduce((max, p) => p.date > max ? p.date : max, prices[0]?.date || '') : 'N/A'}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchData} className="btn-secondary" data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4 mr-2" /> Actualiser
          </Button>
          {prices.length === 0 && (
            <Button onClick={handleSeedData} className="btn-primary" data-testid="seed-market-btn">
              Charger données démo
            </Button>
          )}
        </div>
      </div>

      {/* Market Analysis */}
      {analysis?.analysis && (
        <div className="bg-white border border-[#BFC6CC]/12 rounded-2xl p-5 hover:shadow-md transition-shadow duration-200">
          <h2 className="text-lg font-bold text-[#191D1E] mb-4 flex items-center gap-2">
            <Activity className="w-5 h-5 text-[#397DFF]" /> Analyse du marché
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {analysis.analysis.electricity_summary && (
              <div className="p-4 bg-[#F9C80E]/5 rounded-lg border border-[#F9C80E]/20">
                <h4 className="text-sm font-medium text-[#191D1E] mb-2 flex items-center gap-2">
                  <Zap className="w-4 h-4 text-[#F9C80E]" /> Électricité
                </h4>
                <p className="text-sm text-[#687175]">{analysis.analysis.electricity_summary}</p>
              </div>
            )}
            {analysis.analysis.gas_summary && (
              <div className="p-4 bg-[#397DFF]/5 rounded-lg border border-[#397DFF]/20">
                <h4 className="text-sm font-medium text-[#191D1E] mb-2 flex items-center gap-2">
                  <Flame className="w-4 h-4 text-[#397DFF]" /> Gaz Naturel
                </h4>
                <p className="text-sm text-[#687175]">{analysis.analysis.gas_summary}</p>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Price List */}
        <div className="bg-white border border-[#BFC6CC]/12 rounded-2xl p-5 hover:shadow-md transition-shadow duration-200">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="bg-[#F5F6F9] border border-[#BFC6CC]/30 mb-4">
              <TabsTrigger value="electricity" className="data-[state=active]:bg-white data-[state=active]:text-[#191D1E]" data-testid="tab-electricity">
                <Zap className="w-4 h-4 mr-2 text-[#F9C80E]" /> Électricité
              </TabsTrigger>
              <TabsTrigger value="gas" className="data-[state=active]:bg-white data-[state=active]:text-[#191D1E]" data-testid="tab-gas">
                <Flame className="w-4 h-4 mr-2 text-[#397DFF]" /> Gaz
              </TabsTrigger>
            </TabsList>

            <div className="space-y-2 max-h-[500px] overflow-y-auto">
              {currentPrices.length === 0 ? (
                <div className="text-center py-8 text-[#687175]">
                  Aucun prix disponible
                </div>
              ) : (
                currentPrices.map(price => (
                  <PriceRow key={price.contract_name} price={price} />
                ))
              )}
            </div>
          </Tabs>
        </div>

        {/* Chart */}
        <div>
          {selectedContract && (
            <>
              <div className="mb-4 flex items-center gap-4">
                <Select value={selectedContract} onValueChange={setSelectedContract}>
                  <SelectTrigger className="w-full bg-white border-[#BFC6CC] text-[#191D1E]" data-testid="contract-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-[#BFC6CC]">
                    {allContracts.map(p => (
                      <SelectItem key={p.contract_name} value={p.contract_name} className="text-[#191D1E]">
                        {p.contract_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {historyLoading ? (
                <Skeleton className="h-80 bg-white" />
              ) : priceHistory.length > 0 ? (
                <PriceChart
                  data={priceHistory}
                  title={`Évolution - ${selectedContract}`}
                  energyType={currentContractData?.energy_type || 'electricity'}
                  contractName={selectedContract}
                />
              ) : (
                <div className="bg-white border border-[#BFC6CC]/12 rounded-2xl p-8 text-center hover:shadow-md transition-shadow duration-200">
                  <p className="text-[#687175]">Aucun historique disponible</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Indicators */}
      {analysis?.indicators && (
        <div className="bg-white border border-[#BFC6CC]/12 rounded-2xl p-5 hover:shadow-md transition-shadow duration-200">
          <h3 className="text-lg font-bold text-[#191D1E] mb-4">Indicateurs</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {analysis.indicators.co2_price && (
              <div className="p-4 bg-[#F5F6F9] rounded-lg">
                <p className="text-xs text-[#687175] mb-1">CO2 (EUA)</p>
                <p className="font-mono font-bold text-[#191D1E]">{analysis.indicators.co2_price} €/t</p>
              </div>
            )}
            {analysis.indicators.eur_usd && (
              <div className="p-4 bg-[#F5F6F9] rounded-lg">
                <p className="text-xs text-[#687175] mb-1">EUR/USD</p>
                <p className="font-mono font-bold text-[#191D1E]">{analysis.indicators.eur_usd}</p>
              </div>
            )}
            {analysis.indicators.brent_price && (
              <div className="p-4 bg-[#F5F6F9] rounded-lg">
                <p className="text-xs text-[#687175] mb-1">Brent</p>
                <p className="font-mono font-bold text-[#191D1E]">${analysis.indicators.brent_price}/bbl</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MarketPage;
